<script setup lang="ts">
import { useArticleStore } from "~/store/article";
import { useArticleThemeStore, type ArticleTheme } from "~/store/article/theme";
import type { ArticleSerie } from "./articleTypes";

const route = useRoute();
const cat = route.params.cat;
const articleThemeStore = useArticleThemeStore();
const articleStore = useArticleStore();
const isLoading = ref(false);
const router = useRouter();
const actionTheme = ref<ArticleSerie | null>(null);
const currentCategory = computed(() => {
  return (
    articleStore.categories.find((category) => category.frontRoute === cat) ||
    null
  );
});

const fetchCategories = async () => {
  isLoading.value = true;
  await articleStore.getCategories();
  isLoading.value = false;
};

const onSelectTheme = (theme: ArticleTheme) => {
  articleThemeStore.setSelectedTheme(theme);
  router.push(`/articles/${currentCategory.value?.frontRoute}`);
};

const onSelectAll = () => {
  articleThemeStore.setSelectedTheme(null);
  router.push(`/articles/${currentCategory.value?.frontRoute}`);
};

const onSelectNoTheme = () => {
  articleThemeStore.setSelectedTheme("no-theme");
  router.push(`/articles/${currentCategory.value?.frontRoute}`);
};

watch(currentCategory, async () => {
  if (currentCategory.value) {
    await articleThemeStore.getThemesByCategory(currentCategory.value._id);
  }
});

onMounted(async () => {
  isLoading.value = true;
  await fetchCategories();
  isLoading.value = false;
});
</script>
<template>
  <div data-simplebar class="nicescroll-bar">
    <div class="menu-content-wrap">
      <div class="d-flex align-items-center justify-content-between">
        <div class="title-sm text-primary mb-0">Thèmes</div>
        <a
          href="#"
          class="btn btn-xs btn-icon btn-rounded btn-primary"
          data-bs-toggle="modal"
          data-bs-target="#add_new_article_theme"
          data-bs-type="add"
          ><span id="addType" class="icon"
            ><span class="feather-icon"><i class="bx bx-plus"></i></span></span
        ></a>
      </div>
      <div class="menu-group">
        <ul class="nav nav-light navbar-nav flex-column">
          <!-- Section Tous -->
          <li
            class="d-flex align-items-center theme-item w-100 mw-100 mb-2"
            @click="onSelectAll"
          >
            <div
              class="nav-link link-badge-right flex-grow-1 text-truncate mw-0 d-flex align-items-center"
            >
              <i class="bx bx-grid-alt text-muted me-2"></i>
              <span
                :class="
                  !articleThemeStore.selectedTheme
                    ? 'text-primary fw-semibold'
                    : 'text-muted'
                "
              >
                Tous
              </span>
            </div>
          </li>

          <!-- Section Sans thème -->
          <li
            class="d-flex align-items-center theme-item w-100 mw-100 mb-2"
            @click="onSelectNoTheme"
          >
            <div
              class="nav-link link-badge-right flex-grow-1 text-truncate mw-0 d-flex align-items-center"
            >
              <i class="bx bx-minus-circle text-muted me-2"></i>
              <span
                :class="
                  articleThemeStore.selectedTheme === 'no-theme'
                    ? 'text-primary fw-semibold'
                    : 'text-muted'
                "
              >
                Sans thème
              </span>
            </div>
          </li>

          <!-- Séparateur -->
          <li class="nav-item">
            <hr class="my-2 text-muted" />
          </li>

          <!-- Liste des thèmes -->
          <li
            class="d-flex align-items-center theme-item w-100 mw-100"
            v-for="theme in articleThemeStore.themes"
            :key="theme._id"
            @click="() => onSelectTheme(theme)"
          >
            <div
              class="nav-link link-badge-right flex-grow-1 text-truncate mw-0 d-flex align-items-center"
            >
              <i class="bx bx-tag text-muted me-2"></i>
              <p class="flex-grow-1 text-truncate mw-0" :title="theme.name">
                <span
                  :class="
                    articleThemeStore.selectedTheme !== 'no-theme' &&
                    theme.name === articleThemeStore.selectedTheme?.name
                      ? 'text-primary fw-semibold'
                      : 'text-muted'
                  "
                >
                  {{ theme.name }}
                </span>
              </p>
            </div>
            <div class="dropdown flex-shrink-0">
              <i
                id="theme-ellipsis"
                class="bx bx-dots-horizontal-rounded opacity-0 menu-ellipsis"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                @click="(e) => e.stopPropagation()"
              />
              <ul class="dropdown-menu h-min" aria-labelledby="theme-ellipsis">
                <li>
                  <p
                    class="dropdown-item"
                    @click="
                      (e) => {
                        e.stopPropagation();
                        actionTheme = theme;
                      }
                    "
                    data-bs-toggle="modal"
                    data-bs-target="#add_new_article_theme"
                    data-bs-type="edit"
                  >
                    Modifier
                  </p>
                </li>
                <li>
                  <p
                    class="dropdown-item"
                    @click="
                      (e) => {
                        e.stopPropagation();
                        actionTheme = theme;
                      }
                    "
                    data-bs-toggle="modal"
                    data-bs-target="#delete_article_theme"
                  >
                    Supprimer
                  </p>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <ArticlesThemeAddModal
    :theme="actionTheme"
    :currentCategory="currentCategory"
  />
  <ArticlesThemeDeleteModal
    :theme="actionTheme"
    :currentCategory="currentCategory"
  />
</template>
<style scoped>
.nav-link {
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.theme-item {
  padding: 0rem 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme-item:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.theme-item:hover .nav-link {
  background-color: transparent;
}

hr {
  border-color: rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
}

.menu-ellipsis {
  opacity: 0;
  cursor: pointer;
}

.theme-item:hover .menu-ellipsis {
  opacity: 1 !important;
}

.dropdown-item {
  font-size: 14px !important;
}
</style>
